export class IconAnimation {
    constructor(img) {
        this.img = img;
        this.isAnimating = false;
        this.animationTimeout = null;
        
        try {
            this.frames = JSON.parse(img.dataset.frames || '[]');
            this.options = JSON.parse(img.dataset.animationOptions || '{}');
            this.originalSrc = this.frames[0]?.src || img.src;
            
            if (this.frames.length > 0) {
                if (!this.options.hover) {
                    this.startAnimation();
                } else {
                    this.setupHoverEvents();
                }
            }
        } catch (e) {
            console.warn('Error initializing animation:', e);
        }
    }

    clearTimeouts() {
        if (this.animationTimeout) {
            clearTimeout(this.animationTimeout);
            this.animationTimeout = null;
        }
    }

    async animate(reverse = false, isPartOfSequence = false) {
        if (!this.isAnimating) return;

        const frames = reverse ? [...this.frames].reverse() : this.frames;
        let currentFrame = 0;

        const nextFrame = () => {
            if (!this.isAnimating || currentFrame >= frames.length) {
                if (!isPartOfSequence && this.options.reverseOnComplete && !reverse && this.isAnimating) {
                    // Start reverse animation when forward animation completes
                    this.animate(true, true);
                } else if (this.options.loop && this.isAnimating && reverse) {
                    // If we completed a reverse animation and loop is enabled, 
                    // wait interval time and start the sequence again
                    if (this.options.interval > 0) {
                        this.animationTimeout = setTimeout(() => this.animate(false, false), this.options.interval);
                    } else {
                        this.animate(false, false);
                    }
                } else if (this.options.loop && this.isAnimating && !reverse && !this.options.reverseOnComplete) {
                    // If loop is enabled but reverseOnComplete is not, just loop the forward animation
                    if (this.options.interval > 0) {
                        this.animationTimeout = setTimeout(() => this.animate(false, false), this.options.interval);
                    } else {
                        this.animate(false, false);
                    }
                }
                return;
            }

            this.img.src = frames[currentFrame].src;
            currentFrame++;
            this.animationTimeout = setTimeout(() => nextFrame(), frames[currentFrame - 1].duration);
        };

        nextFrame();
    }

    startAnimation() {
        this.stopAnimation();
        this.isAnimating = true;
        this.animate(false, false);
    }

    stopAnimation() {
        this.isAnimating = false;
        this.clearTimeouts();
        this.img.src = this.originalSrc;
    }

    getTotalDuration() {
        return this.frames.reduce((total, frame) => total + frame.duration, 0);
    }

    setupHoverEvents() {
        const handleMouseEnter = () => {
            this.isAnimating = true;
            this.animate(false, false);
        };

        const handleMouseLeave = () => {
            this.isAnimating = false;
            this.clearTimeouts();
            
            if (this.options.reverseOnHoverEnd) {
                this.isAnimating = true;
                this.animate(true, false);
                setTimeout(() => {
                    this.isAnimating = false;
                    this.clearTimeouts();
                }, this.getTotalDuration());
            } else {
                this.img.src = this.originalSrc;
            }
        };

        this.img.addEventListener('mouseenter', handleMouseEnter);
        this.img.addEventListener('mouseleave', handleMouseLeave);
        this.img.addEventListener('touchstart', (e) => {
            e.preventDefault();
            handleMouseEnter();
        });
        this.img.addEventListener('touchend', handleMouseLeave);
    }
}

export const initializeAnimations = (selector = 'img[data-frames]') => {
    document.querySelectorAll(selector).forEach(img => {
        if (!img._iconAnimation) {
            img._iconAnimation = new IconAnimation(img);
        }
    });
};

export const observeAnimations = (selector = 'img[data-frames]') => {
    const observer = new MutationObserver((mutations) => {
        mutations.forEach(mutation => {
            mutation.addedNodes.forEach(node => {
                if (node.nodeType === 1) {
                    const images = node.querySelectorAll ? 
                        node.querySelectorAll(selector) : [];
                    
                    images.forEach(img => {
                        if (!img._iconAnimation) {
                            img._iconAnimation = new IconAnimation(img);
                        }
                    });
                }
            });
        });
    });

    observer.observe(document.body, {
        childList: true,
        subtree: true
    });

    return observer;
};